/**
 * The admin's to-do list, built once and shown in two places.
 *
 * There were three "needs attention" lists — Overview, Governance and Data
 * sources — and no two of them counted the same things, so none of their
 * numbers could be trusted to mean "this is how far from done we are".
 * Every open item is now derived here, from the same data, and grouped by
 * WHO has to act: a platform admin cannot sign an agreement, and a data
 * owner cannot classify a risk. Overview shows all of it; the Compliance
 * report shows the compliance part of the same list.
 *
 * Every item names the objects it is about, and each is a link to the
 * place where it is resolved — a status that cannot be acted on is noise.
 */
import type { DataSource, GovernanceAssistant } from "../api/admin";
import { adminLink } from "./vocabulary";

export type Actor = "admin" | "signers" | "data-owner" | "board" | "owners";

export const ACTORS: { key: Actor; label: string; hint: string }[] = [
  { key: "admin", label: "Platform admin", hint: "you, running DOIT AI" },
  {
    key: "signers",
    label: "Agreement signers",
    hint: "IT management, data owners and Group Compliance",
  },
  {
    key: "data-owner",
    label: "Data owners",
    hint: "confirm who may see their data",
  },
  {
    key: "board",
    label: "AI Board",
    hint: "accept assistants for use",
  },
  { key: "owners", label: "Assistant owners", hint: "act on feedback" },
];

export type Target = { label: string; link: string };

export type TodoItem = {
  key: string;
  actor: Actor;
  n: number;
  /** Reads after the number: "3 agreements waiting for signatures". */
  text: string;
  tone: "warn" | "bad";
  /** Where the whole list lives. */
  link: string;
  /** The specific things, each linking to where it is resolved. */
  targets: Target[];
  /** Part of the compliance record (shown on the Compliance report too). */
  compliance: boolean;
};

function plural(n: number, one: string, many = `${one}s`): string {
  return n === 1 ? one : many;
}

const sourceLink = (s: DataSource) =>
  adminLink("data-sources", { source: s.key });
const assistantLink = (a: GovernanceAssistant, section = "compliance") =>
  adminLink("assistants", { assistant: a.slug, section });

export function sourceTodos(sources: DataSource[]): TodoItem[] {
  const out: TodoItem[] = [];
  const t = (s: DataSource): Target => ({
    label: s.label,
    link: sourceLink(s),
  });

  const overdue = sources.filter((s) => s.health === "overdue");
  const failing = sources.filter((s) => s.health === "failing");
  if (overdue.length)
    out.push({
      key: "source-overdue",
      actor: "admin",
      n: overdue.length,
      text: `${plural(overdue.length, "data source")} not refreshing on schedule`,
      tone: "bad",
      link: adminLink("data-sources"),
      targets: overdue.map(t),
      compliance: false,
    });
  if (failing.length)
    out.push({
      key: "source-failing",
      actor: "admin",
      n: failing.length,
      text: `${plural(failing.length, "data source")} whose last refresh failed`,
      tone: "bad",
      link: adminLink("data-sources"),
      targets: failing.map(t),
      compliance: false,
    });

  const broken = sources.filter(
    (s) => s.agreement_state === "blocked" || s.agreement_state === "missing",
  );
  if (broken.length)
    out.push({
      key: "agreement-blocked",
      actor: "admin",
      n: broken.length,
      text: `${plural(broken.length, "agreement")} missing values or not found`,
      tone: "bad",
      link: adminLink("data-sources"),
      targets: broken.map(t),
      compliance: true,
    });

  const drafts = sources.filter((s) => s.agreement_state === "draft");
  if (drafts.length)
    out.push({
      key: "agreement-draft",
      actor: "signers",
      n: drafts.length,
      text: `${plural(drafts.length, "agreement")} waiting for signatures`,
      tone: "warn",
      link: adminLink("data-sources"),
      targets: drafts.map((s) => {
        const m = s.agreement_meta;
        return {
          label: m
            ? `${s.label} (${m.signatures.dated}/${m.signatures.needed})`
            : s.label,
          link: sourceLink(s),
        };
      }),
      compliance: true,
    });
  return out;
}

/** Assistant to-dos, from the same three steps the Approvals page shows
 * (ADR 0066, 2026-09-27 review) — one per step that is open, grouped by
 * who takes it. Safeguard warnings are evidence, not tasks, and are not
 * counted here; neither is a review date, which the acceptance sets. */
const STEP_ITEMS: {
  key: string;
  actor: Actor;
  section: string;
  text: (n: number) => string;
  hit: (a: GovernanceAssistant) => boolean;
}[] = [
  {
    key: "confirm",
    actor: "data-owner",
    section: "access",
    text: (n) =>
      `${plural(n, "assistant")} waiting for a data owner to confirm who may see their data`,
    hit: (a) => step(a, "data_owners") === "todo",
  },
  {
    key: "tests",
    actor: "admin",
    section: "access",
    text: (n) => `${plural(n, "assistant")} whose access tests are missing or failing`,
    hit: (a) => step(a, "tests") === "todo",
  },
  {
    key: "record",
    actor: "admin",
    section: "compliance",
    text: (n) =>
      `${plural(n, "assistant")} missing an owner, risk class or intended use — needed before acceptance`,
    hit: (a) => !!a.readiness?.record_missing?.length,
  },
  {
    key: "accept",
    actor: "board",
    section: "compliance",
    text: (n) => `${plural(n, "assistant")} ready for the AI Board to accept`,
    hit: (a) => step(a, "accepted") === "todo" && !!a.readiness?.can_accept,
  },
];

function step(a: GovernanceAssistant, key: string): string | undefined {
  return a.readiness?.steps.find((s) => s.key === key)?.status;
}

export function assistantTodos(rows: GovernanceAssistant[]): TodoItem[] {
  const out: TodoItem[] = [];
  for (const it of STEP_ITEMS) {
    // Disabled and experimental assistants answer nobody; not today's work.
    const hit = rows.filter(
      (a) => a.enabled && a.maturity !== "experimental" && it.hit(a),
    );
    if (!hit.length) continue;
    out.push({
      key: `step-${it.key}`,
      actor: it.actor,
      n: hit.length,
      text: it.text(hit.length),
      tone: "warn",
      link: adminLink("governance"),
      targets: hit.map((a) => ({
        label: a.name || a.slug,
        link: assistantLink(a, it.section),
      })),
      compliance: true,
    });
  }
  return out;
}

export function groupByActor(
  items: TodoItem[],
): { actor: (typeof ACTORS)[number]; items: TodoItem[]; n: number }[] {
  return ACTORS.map((actor) => {
    const mine = items
      .filter((i) => i.actor === actor.key)
      .sort((a, b) =>
        a.tone === b.tone ? b.n - a.n : a.tone === "bad" ? -1 : 1,
      );
    return { actor, items: mine, n: mine.reduce((s, i) => s + i.n, 0) };
  }).filter((g) => g.items.length);
}
